import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Settings, LogOut, AlertTriangle, Users, UserCircle, HelpCircle, Mail, Clock, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ModuleSelector = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingReminders, setUpcomingReminders] = useState([]);

  useEffect(() => {
    checkSubscription();
    loadUpcomingReminders();
  }, [user.user_id]);

  const loadUpcomingReminders = async () => {
    try {
      const res = await axios.get(`${API}/reminders`, {
        params: { user_id: user.user_id, status: 'pending', upcoming_days: 7 },
      });
      setUpcomingReminders(res.data.reminders || []);
    } catch (e) {
      // non-fatal
    }
  };

  const checkSubscription = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.user_id}/subscription-status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show expired subscription message
  if (!loading && subscriptionStatus?.status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="h-8 w-8 text-rose-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Η Συνδρομή σας Έληξε
          </h2>
          <p className="text-slate-600 mb-6">
            Η συνδρομή σας στο ESPA Manager έχει λήξει. Παρακαλούμε επικοινωνήστε με το support για ανανέωση.
          </p>
          <div className="space-y-3">
            <a
              href="mailto:espamanagerapp@gmail.com"
              className="block w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Επικοινωνία με Support
            </a>
            <Button
              onClick={onLogout}
              variant="outline"
              className="w-full"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Αποσύνδεση
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <h1 className="text-xl md:text-2xl font-bold text-white">ESPA Manager</h1>
          <div className="flex flex-wrap items-center gap-2 md:gap-4">
            {subscriptionStatus && (
              <div className="text-xs text-white/70 text-right">
                <div>{subscriptionStatus.package_type?.toUpperCase()}</div>
                {subscriptionStatus.days_until_expiry > 0 && (
                  <div className={subscriptionStatus.days_until_expiry <= 15 ? 'text-amber-400' : ''}>
                    {subscriptionStatus.days_until_expiry} ημέρες
                  </div>
                )}
              </div>
            )}
            <span className="text-white/70 text-xs md:text-sm hidden md:inline">{user.email}</span>
            {user.is_admin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="text-white/70 hover:text-white hover:bg-white/10 text-xs md:text-sm px-2 md:px-3"
              >
                <Settings className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Admin</span>
              </Button>
            )}
            {user.role !== 'member' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/team')}
                className="text-white/70 hover:text-white hover:bg-white/10 text-xs md:text-sm px-2 md:px-3"
              >
                <Users className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Ομάδα</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="text-white/70 hover:text-white hover:bg-white/10 text-xs md:text-sm px-2 md:px-3"
            >
              <LogOut className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Έξοδος</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Subscription Warning Banner */}
      {subscriptionStatus?.status === 'expiring_soon' && (
        <div className="bg-amber-500 text-amber-900 px-6 py-2 text-center text-sm">
          ⚠️ Η συνδρομή σας λήγει σε {subscriptionStatus.days_until_expiry} ημέρες. Επικοινωνήστε με το support για ανανέωση.
        </div>
      )}

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Διαχείριση Επενδυτικών Σχεδίων
          </h2>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
            Επιλέξτε τον τομέα που θέλετε να διαχειριστείτε
          </p>
        </div>

        {/* Hero Image - Hidden on mobile */}
        <div className="relative mb-8 md:mb-12 rounded-2xl overflow-hidden shadow-2xl hidden md:block">
          <img
            src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&h=400&fit=crop"
            alt="Business professionals"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent flex items-center">
            <div className="px-12">
              <h3 className="text-3xl font-bold text-white mb-2">
                Καλώς ήρθατε
              </h3>
              <p className="text-white/80 text-lg">
                Ολοκληρωμένη πλατφόρμα διαχείρισης ΕΣΠΑ
              </p>
            </div>
          </div>
        </div>

        {/* Deadlines / Reminders Widget */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-gradient-to-br from-amber-500/10 to-rose-500/10 backdrop-blur-sm rounded-xl md:rounded-2xl border border-amber-400/30 overflow-hidden" data-testid="reminders-widget">
            <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-bold text-white">Προθεσμίες & Υπενθυμίσεις</h3>
                  <p className="text-xs text-white/60">Email την επιλεγμένη ημερομηνία και ώρα</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => navigate('/reminders')}
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs md:text-sm"
                  data-testid="reminders-widget-new-btn">
                  <Plus className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">Νέα</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => navigate('/reminders')}
                  className="text-white/80 hover:text-white hover:bg-white/10 text-xs md:text-sm"
                  data-testid="reminders-widget-all-btn">
                  Προβολή όλων
                </Button>
              </div>
            </div>
            <div className="px-5 md:px-6 py-4">
              {upcomingReminders.length === 0 ? (
                <div className="text-center py-6 text-white/60 text-sm">
                  Καμία υπενθύμιση για τις επόμενες 7 ημέρες. <button onClick={() => navigate('/reminders')} className="text-amber-400 hover:text-amber-300 underline">Δημιουργήστε μία</button>
                </div>
              ) : (
                <div className="space-y-2">
                  {upcomingReminders.slice(0, 5).map((r) => {
                    const dateStr = `${(r.reminder_date || '').split('-').reverse().join('/')} ${r.reminder_time || ''}`;
                    const urgent = r.is_overdue || r.is_today;
                    return (
                      <div key={r.reminder_id}
                        onClick={() => navigate('/reminders')}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${urgent ? 'bg-rose-500/20 border border-rose-400/40 hover:bg-rose-500/30' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`}
                        data-testid={`reminders-widget-item-${r.reminder_id}`}>
                        <div className="flex-shrink-0 flex flex-col items-center justify-center w-12 h-12 bg-white/10 rounded-lg">
                          <div className="text-sm font-bold text-white">{(r.reminder_date || '').split('-')[2]}</div>
                          <div className="text-[9px] text-white/60 uppercase">{r.reminder_time}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-medium text-sm truncate">{r.program_name}</span>
                            {r.is_overdue && <span className="text-[10px] bg-rose-600 text-white px-2 py-0.5 rounded-full animate-pulse" data-testid="widget-overdue-badge">Εκπρόθεσμη</span>}
                            {!r.is_overdue && r.is_today && <span className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full" data-testid="widget-today-badge">Σήμερα</span>}
                            {!r.is_overdue && !r.is_today && r.is_tomorrow && <span className="text-[10px] bg-amber-400/40 text-amber-200 px-2 py-0.5 rounded-full">Αύριο</span>}
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.category === 'application' ? 'bg-emerald-500/30 text-emerald-200' : 'bg-blue-500/30 text-blue-200'}`}>
                              {r.category === 'application' ? 'Αίτηση' : 'Υλοποίηση'}
                            </span>
                          </div>
                          <div className="text-xs text-white/60 truncate mt-0.5">{r.client_name} • {dateStr}</div>
                        </div>
                      </div>
                    );
                  })}
                  {upcomingReminders.length > 5 && (
                    <div className="text-center text-xs text-white/50 pt-1">+{upcomingReminders.length - 5} ακόμη</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Module Cards */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-4xl mx-auto">
          {/* Αίτηση Card */}
          <div
            onClick={() => navigate('/dashboard')}
            className="group cursor-pointer bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-6 md:p-8 border border-white/20 hover:bg-white/20 hover:border-emerald-500/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
          >
            <div className="w-12 md:w-16 h-12 md:h-16 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-emerald-500/30 transition-colors">
              <FileText className="h-6 md:h-8 w-6 md:w-8 text-emerald-400" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">
              Αίτηση Επενδυτικού Σχεδίου
            </h3>
            <p className="text-sm md:text-base text-white/70 mb-4 md:mb-6">
              Διαχείριση αιτήσεων, δικαιολογητικών και συνεργατών για νέα επενδυτικά σχέδια
            </p>
            <div className="flex items-center text-emerald-400 font-medium text-sm md:text-base">
              Είσοδος
              <svg className="w-4 md:w-5 h-4 md:h-5 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>

          {/* Υλοποίηση Card */}
          <div
            onClick={() => navigate('/implementation')}
            className="group cursor-pointer bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-6 md:p-8 border border-white/20 hover:bg-white/20 hover:border-blue-500/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
          >
            <div className="w-12 md:w-16 h-12 md:h-16 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-blue-500/30 transition-colors">
              <Settings className="h-6 md:h-8 w-6 md:w-8 text-blue-400" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">
              Υλοποίηση Επενδυτικού Σχεδίου
            </h3>
            <p className="text-sm md:text-base text-white/70 mb-4 md:mb-6">
              Παρακολούθηση τιμολογίων, δικαιολογητικών υλοποίησης και προμηθευτών
            </p>
            <div className="flex items-center text-blue-400 font-medium text-sm md:text-base">
              Είσοδος
              <svg className="w-4 md:w-5 h-4 md:h-5 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>
        </div>

        {/* Templates Cards */}
        {user.role !== 'member' && (
          <div className="max-w-4xl mx-auto mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Πρότυπα Αιτήσεων */}
            <div
              onClick={() => navigate('/application-templates')}
              className="group cursor-pointer bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                  <FileText className="h-6 w-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">
                    Πρότυπα Αιτήσεων
                  </h3>
                  <p className="text-white/60 text-sm">
                    Δημιουργήστε πρότυπα για γρήγορη δημιουργία αιτήσεων
                  </p>
                </div>
                <svg className="w-5 h-5 text-purple-400 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </div>

            {/* Πρότυπα Υλοποίησης */}
            <div
              onClick={() => navigate('/implementation-templates')}
              className="group cursor-pointer bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 hover:border-orange-500/50 transition-all duration-300"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center group-hover:bg-orange-500/30 transition-colors">
                  <FileText className="h-6 w-6 text-orange-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">
                    Πρότυπα Υλοποίησης
                  </h3>
                  <p className="text-white/60 text-sm">
                    Δημιουργήστε πρότυπα για γρήγορη δημιουργία υλοποιήσεων
                  </p>
                </div>
                <svg className="w-5 h-5 text-orange-400 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </div>
          </div>
        )}
        
        {/* Notification Assistant Card */}
        <div className="max-w-4xl mx-auto mt-6">
          <div
            onClick={() => navigate('/notification-assistant')}
            className="group cursor-pointer bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300"
            data-testid="notification-assistant-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                <Mail className="h-6 w-6 text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">
                  Βοηθός Ειδοποιήσεων Συμβούλου
                </h3>
                <p className="text-white/60 text-sm">
                  Χειροκίνητη αποστολή υπενθυμίσεων για εκκρεμή δικαιολογητικά αιτήσεων & υλοποιήσεων
                </p>
              </div>
              <svg className="w-5 h-5 text-purple-400 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* CRM Card */}
        <div className="max-w-4xl mx-auto mt-6">
          <div
            onClick={() => navigate('/crm')}
            className="group cursor-pointer bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 hover:border-rose-500/50 transition-all duration-300"
            data-testid="crm-module-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-rose-500/20 rounded-lg flex items-center justify-center group-hover:bg-rose-500/30 transition-colors">
                <UserCircle className="h-6 w-6 text-rose-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">
                  Πελατολόγιο CRM
                </h3>
                <p className="text-white/60 text-sm">
                  Διαχείριση πελατών, επικοινωνιών, ραντεβού και προγραμμάτων ενδιαφέροντος
                </p>
              </div>
              <svg className="w-5 h-5 text-rose-400 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Newsletter Module Card */}
        <div className="max-w-4xl mx-auto mt-6">
          <div
            onClick={() => navigate('/newsletter')}
            className="group cursor-pointer bg-gradient-to-r from-pink-500/10 to-amber-500/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 hover:border-pink-500/50 transition-all duration-300"
            data-testid="newsletter-module-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center group-hover:bg-pink-500/30 transition-colors">
                <Mail className="h-6 w-6 text-pink-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">
                  Newsletter Manager
                </h3>
                <p className="text-white/60 text-sm">
                  Δημιουργήστε επαγγελματικά newsletters με AI & στείλτε τα από το Gmail σας στους πελάτες
                </p>
              </div>
              <svg className="w-5 h-5 text-pink-400 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Help Center Card */}
        <div className="max-w-4xl mx-auto mt-6">
          <div
            onClick={() => navigate('/help-center')}
            className="group cursor-pointer bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 hover:border-cyan-500/50 transition-all duration-300"
            data-testid="help-center-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                <HelpCircle className="h-6 w-6 text-cyan-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">
                  Κέντρο Βοήθειας
                </h3>
                <p className="text-white/60 text-sm">
                  Οδηγίες χρήσης, συχνές ερωτήσεις και AI βοηθός υποστήριξης
                </p>
              </div>
              <svg className="w-5 h-5 text-cyan-400 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleSelector;
